import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { ColDef } from "ag-grid-community";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useRecoilValue } from "recoil";
import { FormEvent, useState } from "react";
import { Sensor } from "../../common/types/sensor";
import { request } from "../../common/api";
import { currentShipState } from "../../common/states/ship";
import { validateDateRange } from "../../common/functions/validateFunctions";
import useSensorSearchInputData from "../../common/hooks/useSensorSearchInputData";
import {
  GET_SENSOR_DATA,
  GET_SENSOR_DATA_COUNT,
} from "../../common/query-keys";
import { Buttons } from "../../components/atoms/Buttons";
import { showAlert } from "../../components/containers/Alert";
import { SearchBar } from "../../components/molecules/SearchBar";
import { InputText } from "../../components/atoms/Inputs/InputText";
import { InputSelect } from "../../components/atoms/Inputs/InputSelect";
import { PageContent } from "../../components/organisms/Contents/PageContent";
import { ServerSideGrid } from "../../components/molecules/ServerSideGrid";
import { ModalLoading } from "../../components/molecules/Modals/ModalLoading";
import {
  startDateHandler,
  startDateHourHandler,
} from "../../common/util/setEndDateAuto";
import { beforeWeekTime } from "../../common/util/beforeWeek";

export interface DataSensorSearchInputs {
  ssrShiptypeNo?: string;
  ssrShiptypeName?: string;
  ssrShipName?: string;
  ssrShipNo?: string;
  ssrMcyName?: string;
  ssrMachine?: string;
  ssrLowMcyName?: string;
  ssrName?: string;
  ssrValue?: string;
  ssrSignal?: string;
  startDate?: string;
  endDate?: string;
  ssrTime?: string;
}

interface SensorDataTypes {
  shiptypeName: string;
  shipName: string;
  machineName: string;
  equipment: string;
  sensorCode: string;
  sensor: string;
  sensorValue: number;
  createdTime: string;
  data_channel_id?: number;
}

export const SensorPage = () => {
  const currentShip = useRecoilValue(currentShipState);
  const searchDefaultValues = {
    // ssrShiptypeName: currentShip.shipTypeName,
    // ssrShipNo: currentShip.shipNo,
    ssrShiptypeNo: currentShip.shipTypeNo || "",
    ssrShiptypeName: currentShip.shipTypeName || "",
    ssrShipNo: currentShip.shipNo || "",
    ssrShipName: currentShip.shipName || "",
    ssrMcyName: "",
    ssrMachine: "",
    ssrLowMcyName: "",
    ssrName: "",
    startDate: dayjs().subtract(6, "days").format("YYYY-MM-DD 00:00:00"),
    endDate: dayjs().format("YYYY-MM-DD 23:59:59"),
  };
  const [searchInputs, setSearchInputs] = useState<DataSensorSearchInputs>();
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [exportExcelLoading, setExportExcelLoading] = useState<boolean>(false);
  const {
    search,
    machinerySelectOptions,
    lowMachinerySelectOptions,
    sensorSelectOptions,
    handleMcySelectChange,
    handleLowMcySelectChange,
    handleSensorSelectChange,
    handleValueChange,
    setSearchData,
  } = useSensorSearchInputData(searchDefaultValues);

  const { data: total, isLoading: isLoadingTotal } = useQuery({
    queryKey: [GET_SENSOR_DATA_COUNT, searchInputs],
    queryFn: async () => {
      return await request<number>({
        method: "GET",
        url: `/sensor/count`,
        params: searchInputs,
      });
    },
    enabled: !!searchInputs,
  });

  // 센서 데이터 조회
  const { data: sensorData, isLoading: isLoadingData } = useQuery({
    queryKey: [GET_SENSOR_DATA, searchInputs, currentPage],
    queryFn: async () => {
      return await request<Sensor[]>({
        method: "GET",
        url: `/sensor`,
        params: { ...searchInputs, currentPage: currentPage },
      });
    },
    enabled: !!searchInputs && !!total,
  });

  /**  csv파일 만들기위한 전체 데이터  */
  const { mutateAsync: getAllSensorData } = useMutation({
    mutationFn: () =>
      request<Promise<SensorDataTypes[] | any>>({
        method: "POST",
        url: "/sensor",
        data: { ...searchInputs },
      }),
  });

  /** 조회 버튼 클릭시 */
  const handleSearch = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(0);
    const { startDate, endDate } = search;

    // 입력 필수값 체크
    if (
      search.ssrMcyName === "" ||
      search.ssrMcyName === "장비 선택" ||
      search.ssrLowMcyName === "" ||
      search.ssrLowMcyName === "계통 선택" ||
      search.ssrName === "" ||
      search.ssrName === "센서명"
    ) {
      showAlert(
        `${
          search.ssrMcyName === "" || search.ssrMcyName === "장비 선택"
            ? "장비, "
            : ""
        } 
        ${
          search.ssrLowMcyName === "" || search.ssrLowMcyName === "계통 선택"
            ? "계통,"
            : ""
        } 
        ${
          search.ssrName === "" || search.ssrName === "센서명" ? "센서명 " : ""
        }을 선택해주세요`
      );
      return;
    }

    const isValid = validateDateRange(startDate, endDate, 7);

    if (!isValid) {
      return;
    }

    setSearchInputs(search);
  };

  /** csv 다운로드 */
  const exportToCSV = async () => {
    setExportExcelLoading(true);
    try {
      // 전체 센서 데이터 조회
      const allSensorData: SensorDataTypes[] = await getAllSensorData();
      if (allSensorData.length === 0) {
        return;
      }

      // CSV 파일에서 헤더 이름을 원하는 이름으로 변경
      const headers = {
        shiptypeName: "함형",
        shipName: "함정",
        machineName: "장비",
        equipment: "계통",
        sensorCode: "센서 코드",
        sensor: "센서 이름",
        sensorValue: "센서 값",
        createdTime: "생성 시간",
      };

      // 데이터를 새 헤더로 변환
      const sanitizedData = allSensorData.map((data) => {
        return {
          [headers.shiptypeName]: data.shiptypeName,
          [headers.shipName]: data.shipName,
          [headers.machineName]: data.machineName,
          [headers.equipment]: data.equipment,
          [headers.sensorCode]: data.sensorCode,
          [headers.sensor]: data.sensor,
          [headers.sensorValue]: data.sensorValue,
          [headers.createdTime]: data.createdTime,
        };
      });

      // 데이터를 워크시트로 변환
      const worksheet = XLSX.utils.json_to_sheet(sanitizedData);

      // CSV 파일 생성
      const csvOutput = XLSX.utils.sheet_to_csv(worksheet);

      // CSV 문자열에서 Blob 생성 및 다운로드 트리거
      const blob = new Blob([csvOutput], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // 파일 이름 설정
      const fileName = `${searchInputs?.ssrShiptypeName}_${searchInputs?.ssrShipName}_${searchInputs?.ssrLowMcyName}_센서데이터.csv`;
      link.setAttribute("download", fileName);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.log(e);
    } finally {
      setExportExcelLoading(false);
    }
  };

  // /** Excel 다운로드 */
  // const exportToExcel = (data: Sensor[]) => {
  //   // 각 열의 Header Name 설정
  //   const formattedData = data.map((item, idx) => {
  //     return {
  //       함형: item.ssrShiptypeName,
  //       함정: item.ssrShipName,
  //       장비: item.ssrMachineName,
  //       하위장비: item.ssrEquipment,
  //       "센서+설명": item.ssrName,
  //       센서값: item.ssrValue,
  //       "센서 수집 시간": item.ssrAlarm,
  //     };
  //   });

  //   const ws = XLSX.utils.json_to_sheet(formattedData);

  //   // 각 셀의 너비 설정
  //   const columnWidths = [
  //     { wch: 10 },
  //     { wch: 10 },
  //     { wch: 10 },
  //     { wch: 10 },
  //     { wch: 60 },
  //     { wch: 10 },
  //     { wch: 25 },
  //   ];
  //   ws["!cols"] = columnWidths;

  //   const wb = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(wb, ws, "SensorData");
  //   XLSX.writeFile(wb, "sensor_data.xlsx");
  // };

  const handleDownloadClick = () => {
    if (sensorData && sensorData.length > 0) {
      exportToCSV();
    } else {
      showAlert("조회된 데이터 결과가 없습니다.");
    }
  };

  const columnDefs: ColDef[] = [
    {
      headerName: "장비",
      field: "machineName",
      width: 120,
    },
    {
      headerName: "계통",
      field: "equipment",
      width: 120,
    },
    {
      headerName: "센서코드",
      field: "sensorCode",
      width: 280,
    },
    {
      headerName: "센서명", //왼쪽 정렬
      field: "sensor",
      cellStyle: { textAlign: "left" },
      flex: 1,
    },
    {
      headerName: "센서값", //왼쪽 정렬
      field: "sensorValue",
      cellStyle: { textAlign: "left" },
      width: 120,
    },
    {
      headerName: "센서 수집 시간",
      field: "createdTime",
      valueGetter: (params: any) =>
        dayjs(params.data.createdTime).format("YYYY.MM.DD HH:mm:ss"),
      width: 280,
    },
  ];

  return (
    <PageContent
      depth01Title="데이터 분석"
      depth02Title="데이터 관리"
      depth03Title="센서 데이터 조회"
    >
      <>
        <SearchBar
          onSearch={handleSearch}
          titleButtons={
            <Buttons
              type="button"
              size="md"
              layout="primary"
              label="csv 다운로드"
              onClick={handleDownloadClick}
              disabled={exportExcelLoading}
            />
          }
          searchInput={
            <>
              <InputSelect
                size="md"
                width={100}
                name="ssrShiptypeName"
                placeholder="함형 선택"
                options={[
                  {
                    value: currentShip.shipTypeNo,
                    label: currentShip.shipTypeName,
                  },
                ]}
                value={search.ssrShiptypeNo}
                // options={shipTypeSelectOptions}
                // onChange={handleShipTypeSelectChange}
                disabled={true}
                displayNone={true}
              />
              <InputSelect
                size="md"
                width={100}
                name="ssrShipName"
                placeholder="함정 선택"
                options={[
                  { value: currentShip.shipNo, label: currentShip.shipName },
                ]}
                // options={shipSelectOptions}
                // onChange={handleShipSelectChange}
                disabled={true}
                value={search.ssrShipNo}
                displayNone={true}
              />
              <InputSelect
                size="md"
                width={120}
                name="ssrMcyName"
                placeholder="장비 선택"
                options={machinerySelectOptions}
                onChange={handleMcySelectChange}
                value={search.ssrMachine}
                disabled={!search.ssrShipName}
              />
              <InputSelect
                size="md"
                width={120}
                name="ssrLowMcyName"
                placeholder="계통 선택"
                options={lowMachinerySelectOptions}
                onChange={handleLowMcySelectChange}
                disabled={!search.ssrMcyName}
                value={search.ssrLowMcyName}
              />
              <InputSelect
                size="md"
                width={120}
                name="ssrSignal"
                options={[
                  { label: "디지털", value: "D" },
                  { label: "아날로그", value: "A" },
                ]}
                placeholder="센서 신호 선택"
                onChange={(e) => setSearchData(e.target.name, e.target.value)}
                value={search.ssrSignal}
                disabled={!search.ssrLowMcyName}
              />
              <InputSelect
                size="md"
                width={220}
                name="ssrName"
                placeholder="센서 선택"
                options={sensorSelectOptions}
                onChange={handleSensorSelectChange}
                disabled={!search.ssrLowMcyName}
                value={search.ssrName}
              />
              <InputText
                size="md"
                // type="date"
                type="datetime-local"
                step="1"
                width={200}
                name="startDate"
                endDate={true}
                defaultValue={search?.startDate}
                value={search?.startDate}
                onChange={(e) => startDateHourHandler(e, setSearchData, 7)}
              />
              ~
              <InputText
                size="md"
                // type="date"
                type="datetime-local"
                step="1"
                width={200}
                name="endDate"
                endDate={true}
                defaultValue={search?.endDate}
                value={search?.endDate}
                onChange={handleValueChange}
              />
              <Buttons
                type="submit"
                size="md"
                layout="selectCondition"
                label="전1주"
                onClick={() => beforeWeekTime(search, setSearchData, 7)}
              />
            </>
          }
        />

        <ServerSideGrid
          rowData={sensorData ?? []}
          columnDefs={columnDefs}
          total={total ?? 0}
          defaultPageSize={16}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          isLoading={isLoadingTotal || isLoadingData}
        />
        {exportExcelLoading && (
          <ModalLoading timerRunning={exportExcelLoading} />
        )}
      </>
    </PageContent>
  );
};
